import React, {useState, useCallback, useMemo, Fragment, useEffect} from 'react'
import {
    Box,
    Flex,
    Button,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalHeader,
    ModalOverlay,
    useDisclosure,
    Text,
    Progress,
    IconButton,
    Alert,
    Divider
} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import Review from '../review'
import {FormattedMessage, useIntl} from 'react-intl'
import BackToTopLink from '../back-to-top-link'
import {SearchIcon, AlertIcon} from '../icons'
import useProductReviews from '../../bazaarvoice-api/hooks/use-product-reviews'
import useSearchReviewsFields from '../forms/useSearchReviewsFields'
import {useForm} from 'react-hook-form'
import Field from '../field'
import FilterSortButton from '../filter-sort/filter-sort-button'
import SelectedFilters from '../filter-sort/selected-filters'
import ReviewsFilterSortOverlay, {DEFAULT_SORT_ORDER} from './filter-sort-overlay'
import {updateFilterValue} from '../filter-sort/utils'
import {trackRRElementVisibility} from '../../tracking/analytics'
import LoadingReviewsSkeleton from './loading-reviews-skeleton'

/**
 * ReviewsModal component to display product reviews in a modal.
 *
 * @param {node} children - Child components to render inside the modal body
 * @param {boolean} isOpen - Whether the modal is open
 * @param {function} onClose - Callback to close the modal
 * @param {number} reviewsCount - Total number of reviews for pagination
 * @param {string} productId - Id of the product being reviewed
 */
export const ReviewsModal = ({children, isOpen, onClose, reviewsCount, productId}) => {
    const form = useForm({
        defaultValues: {searchTerm: ''}
    })
    const {reset} = form
    const fields = useSearchReviewsFields({form})
    const {formatMessage} = useIntl()
    const [query, setQuery] = useState({sort: DEFAULT_SORT_ORDER})

    const {
        loading,
        reviews: allReviews,
        totalCount: filteredReviewsCount,
        loadNextPage: loadMore
    } = useProductReviews(productId, {
        ...query,
        active: isOpen
    })
    const searchedValue = query?.searchTerm
    const [scrollContainer, setScrollContainer] = useState(null)
    const overlayDiscloseProps = useDisclosure()

    const handleSearch = useCallback(({searchTerm}) => {
        setQuery((query) =>
            updateFilterValue(query, 'searchTerm', {value: searchTerm}, false, Boolean(searchTerm))
        )
    }, [])

    const handleResetSearch = useCallback(() => {
        reset()
        setQuery((query) => updateFilterValue(query, 'searchTerm', '', false, false))
    }, [reset])

    const resetQuery = useCallback(() => {
        reset()
        setQuery({})
    }, [reset])

    const initializeScrollContainer = useCallback((node) => {
        if (node) {
            setScrollContainer(node.parentElement)
        }
    }, [])

    const selectedFilters = useMemo(
        () => [
            ...(query.rating?.map((rating) => ({
                label: formatMessage(
                    {
                        defaultMessage: '{stars, plural, one {# star} other {# stars}}',
                        id: 'reviews.overlay.filter.rating.option.label'
                    },
                    {stars: rating}
                ),
                filterId: 'rating',
                value: rating,
                multiple: true
            })) || []),
            ...(query.language
                ? [
                      {
                          label: formatMessage({
                              defaultMessage: 'Show only reviews in my language',
                              id: 'reviews.overlay.filter.language.option.label'
                          }),
                          filterId: 'language',
                          value: 1,
                          multiple: false
                      }
                  ]
                : [])
        ],
        [formatMessage, query]
    )
    const removeFilter = useCallback((filter) => {
        setQuery((query) =>
            updateFilterValue(query, filter.filterId, filter, filter.multiple, false)
        )
    }, [])
    const clearFilters = useCallback(() => {
        setQuery((query) => updateFilterValue(query, 'rating', [], false, false))
    }, [])

    useEffect(() => {
        if (isOpen) {
            trackRRElementVisibility()
        }
    }, [isOpen])

    return (
        <>
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                colorScheme="stokkeCore"
                size="xl"
                variant="reviews"
                scrollBehavior="inside"
                blockScrollOnMount={false}
            >
                <ModalOverlay />
                <ModalContent ref={initializeScrollContainer}>
                    <ModalHeader>
                        <FormattedMessage defaultMessage="All reviews" id="reviews.all_reviews" />
                    </ModalHeader>
                    <ModalCloseButton size="lgMin" zIndex={1} />
                    <ModalBody width="full">
                        <Flex direction="column" gap={8}>
                            {children}

                            <form onSubmit={form.handleSubmit(handleSearch)} noValidate>
                                <Field
                                    rightElement={
                                        searchedValue ? (
                                            <Button
                                                variant="link"
                                                type="reset"
                                                onClick={handleResetSearch}
                                            >
                                                <FormattedMessage
                                                    defaultMessage="Delete"
                                                    id="global.delete"
                                                />
                                            </Button>
                                        ) : (
                                            <IconButton
                                                type="submit"
                                                icon={<SearchIcon />}
                                                variant="unstyled"
                                                aria-label={formatMessage({
                                                    id: 'reviews.button.assistive_msg.search',
                                                    defaultMessage: 'Search Reviews'
                                                })}
                                            />
                                        )
                                    }
                                    {...fields.searchTerm}
                                />
                            </form>

                            {form.formState.errors.global && (
                                <Alert status="error">
                                    <AlertIcon color="stokkeFunctional.red" boxSize={4} />
                                    <Text textStyle="Regular Small" ml={3}>
                                        {form.formState.errors.global.message}
                                    </Text>
                                </Alert>
                            )}

                            <Flex alignItems="center" justifyContent="space-between">
                                <FilterSortButton
                                    count={selectedFilters?.length}
                                    onClick={overlayDiscloseProps.onOpen}
                                />
                                <Text color="stokkeGray.accessibleText" textStyle="Regular Small">
                                    <FormattedMessage
                                        defaultMessage="{count, plural, one {# review} other {# reviews}}"
                                        id="reviews.showing_reviews"
                                        values={{count: filteredReviewsCount}}
                                    />
                                </Text>
                            </Flex>
                            {selectedFilters?.length ? (
                                <SelectedFilters
                                    containerProps={{justifyContent: 'flex-start'}}
                                    filters={selectedFilters}
                                    onRemove={removeFilter}
                                    onReset={clearFilters}
                                />
                            ) : null}

                            {searchedValue &&
                                !loading &&
                                (allReviews?.length ? (
                                    <Text textStyle="Regular XLarge">
                                        <FormattedMessage
                                            defaultMessage={'Search results for: "{searchedValue}"'}
                                            id="search_results.info.search_results_for"
                                            values={{searchedValue}}
                                        />
                                    </Text>
                                ) : (
                                    <Text textStyle="Regular XLarge">
                                        <FormattedMessage
                                            defaultMessage={'No results for "{searchedValue}"'}
                                            id="search_results.info.no_results_for"
                                            values={{searchedValue}}
                                        />
                                    </Text>
                                ))}

                            <Flex direction="column" gap={8}>
                                {allReviews?.length || loading ? (
                                    <>
                                        {!loading ? (
                                            <>
                                                {allReviews.map((review, index) => (
                                                    <Fragment key={index}>
                                                        <Divider colorScheme="stokkeGray" />

                                                        <Review key={index} review={review} />
                                                    </Fragment>
                                                ))}
                                                <Divider colorScheme="stokkeGray" />
                                            </>
                                        ) : (
                                            <LoadingReviewsSkeleton />
                                        )}

                                        <Flex direction="column" gap={3} alignItems="center">
                                            <Flex direction="column" gap={4} alignItems="center">
                                                <Box textStyle="Semi Bold Small">
                                                    <FormattedMessage
                                                        defaultMessage="{current} of {total}"
                                                        id="pagination_legend.showing"
                                                        values={{
                                                            current: allReviews.length,
                                                            total: filteredReviewsCount
                                                        }}
                                                    />
                                                </Box>
                                                <Progress
                                                    value={
                                                        (allReviews.length / filteredReviewsCount) *
                                                        100
                                                    }
                                                    height={0.5}
                                                    width="164px"
                                                    background="stokkeGray.mid"
                                                    sx={{
                                                        '& > div': {
                                                            background: 'black'
                                                        }
                                                    }}
                                                />
                                                <Button
                                                    onClick={loadMore}
                                                    isLoading={loading}
                                                    isDisabled={
                                                        allReviews?.length >= filteredReviewsCount
                                                    }
                                                    variant="outline"
                                                    width="244px"
                                                >
                                                    <FormattedMessage
                                                        defaultMessage="Load more"
                                                        id="global.loadmore"
                                                    />
                                                </Button>
                                            </Flex>

                                            <BackToTopLink
                                                mt={4}
                                                mb={10}
                                                scrollContainer={scrollContainer}
                                            />
                                        </Flex>
                                    </>
                                ) : null}
                            </Flex>
                        </Flex>
                    </ModalBody>
                </ModalContent>
            </Modal>
            <ReviewsFilterSortOverlay
                {...overlayDiscloseProps}
                query={query}
                onReset={resetQuery}
                count={filteredReviewsCount}
                productId={productId}
                total={reviewsCount}
                onApply={setQuery}
            />
        </>
    )
}

ReviewsModal.propTypes = {
    children: PropTypes.node.isRequired,
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    reviewsCount: PropTypes.number,
    productId: PropTypes.string.isRequired
}

/**
 * Custom hook for managing the state of the ReviewsModal.
 *
 * @returns {Object} - State and functions related to the ReviewsModal.
 */
export const useReviewsModal = () => {
    const {isOpen, onOpen, onClose} = useDisclosure()
    return {
        isOpen,
        onOpen,
        onClose
    }
}
